import { Button, IconButton } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { HiSearch } from "react-icons/hi";
import { CardStateBreakdownChart } from "../../components/analytics/charts/CardStateBreakdownChart";
import { DifficultyDistributionChart } from "../../components/analytics/charts/DifficultyDistributionChart";
import { RetentionRateChart } from "../../components/analytics/charts/RetentionRateChart";
import { SyllableAnalytics } from "../../components/analytics/charts/SyllableAnalytics";
import { ContentSubNav, type ContentSubTab } from "../../components/analytics/layout/ContentSubNav";
import { ReviewComplianceTable } from "../../components/analytics/tables/ReviewComplianceTable";
import { CardsTab } from "../../components/analytics/tabs/-CardsTab";
import { LessonsTab } from "../../components/analytics/tabs/-LessonsTab";
import { OverviewTab } from "../../components/analytics/tabs/-OverviewTab";
import { PatternsTab } from "../../components/analytics/tabs/-PatternsTab";
import { SyllablesTab } from "../../components/analytics/tabs/-SyllablesTab";
import { WordsTab } from "../../components/analytics/tabs/-WordsTab";
import {
  AnalyticsLessonsDocument,
  AnalyticsOtherUsersDocument,
  AnalyticsProjectsDocument,
  AnalyticsSchoolsDocument,
  AnalyticsSlidesDocument,
  AnalyticsStudentsDocument,
} from "../../generated/graphql";

export interface FilterState {
  selectedStudentId: string;
  selectedProjectId: string;
  selectedLessonId: string;
  selectedSlideId: string;
  selectedSchoolId: string;
  selectedOtherUserId: string;
}

export function AnalyticsDashboardPage() {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [selectedLessonId, setSelectedLessonId] = useState<string>("");
  const [selectedSlideId, setSelectedSlideId] = useState<string>("");
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>("");
  const [selectedOtherUserId, setSelectedOtherUserId] = useState<string>("");
  const [activeSubTab, setActiveSubTab] = useState<ContentSubTab>("overview");

  // Fetch dropdown options
  const { data: schoolsData } = useQuery(AnalyticsSchoolsDocument);
  const { data: studentsData } = useQuery(AnalyticsStudentsDocument, {
    variables: { schoolId: selectedSchoolId },
    skip: !selectedSchoolId,
  });
  const { data: otherUsersData } = useQuery(AnalyticsOtherUsersDocument);
  const { data: projectsData } = useQuery(AnalyticsProjectsDocument);
  const { data: lessonsData } = useQuery(AnalyticsLessonsDocument, {
    variables: { parentId: selectedProjectId || undefined },
    skip: !selectedProjectId,
  });
  const { data: slidesData } = useQuery(AnalyticsSlidesDocument, {
    variables: { lessonId: selectedLessonId || "" },
    skip: !selectedLessonId,
  });

  // Filter dropdowns based on search
  const filteredSchools = schoolsData?.accessibleSchools?.filter((school) =>
    school.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredProjects = projectsData?.rootProjects?.items?.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredLessons = lessonsData?.lessonsByParentId?.items?.filter((lesson) =>
    lesson.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter and format students (STUDENT, TEACHER, PRINCIPAL only)
  const allowedRoles = ["STUDENT", "TEACHER", "PRINCIPAL"];
  const filteredStudents = studentsData?.usersBySchoolId
    ?.filter((user) => allowedRoles.includes(user.role))
    .map((user) => ({
      ...user,
      displayName:
        user.role === "TEACHER" || user.role === "PRINCIPAL"
          ? `${user.username} (${user.role.toLowerCase()})`
          : user.username,
    }));

  // Filter other users (all users not in allowedRoles)
  const filteredOtherUsers = otherUsersData?.users
    ?.filter((user) => !allowedRoles.includes(user.role))
    .map((user) => ({
      ...user,
      displayName: `${user.username} (${user.role.toLowerCase()})`,
    }));

  // Check if any filters are active
  const hasActiveFilters =
    selectedSchoolId ||
    selectedStudentId ||
    selectedProjectId ||
    selectedLessonId ||
    selectedSlideId ||
    selectedOtherUserId;

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedSchoolId("");
    setSelectedStudentId("");
    setSelectedProjectId("");
    setSelectedLessonId("");
    setSelectedSlideId("");
    setSelectedOtherUserId("");
  };

  // Create filter state object for tabs
  const filterState: FilterState = {
    selectedStudentId,
    selectedProjectId,
    selectedLessonId,
    selectedSlideId,
    selectedSchoolId,
    selectedOtherUserId,
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="h3">FSRS Analytics Dashboard</h1>

        {/* Search Bar */}
        <div className="relative w-96">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <HiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search schools, projects, or lessons..."
            className="block w-full pl-10 pr-20 py-2 bg-surface border border-gray-800 rounded-md leading-5 text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm"
          />
          {searchQuery && (
            <Button
              type="button"
              onClick={() => setSearchQuery("")}
              variant="ghost"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-primary-600 hover:text-primary-800 font-medium"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="panel p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="h5">Filters</h2>
          {hasActiveFilters && (
            <Button
              type="button"
              onClick={clearAllFilters}
              variant="secondary"
              className="text-primary-600 border-primary-600 hover:text-white hover:bg-primary-600"
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Row 1: School and Student */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* School Dropdown */}
          <div>
            <label className="block label mb-2">School</label>
            <select
              value={selectedSchoolId}
              onChange={(e) => {
                setSelectedSchoolId(e.target.value);
                setSelectedStudentId(""); // Reset student when school changes
              }}
              className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select a school</option>
              {filteredSchools?.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>

          {/* Student Dropdown */}
          <div>
            <label className="block label mb-2">Student</label>
            <select
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
              disabled={!selectedSchoolId}
              className={`w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-900 disabled:cursor-not-allowed ${
                selectedSchoolId && filteredStudents?.length === 0 && selectedStudentId === ""
                  ? "text-gray-500"
                  : ""
              }`}
            >
              <option value="">
                {selectedSchoolId && filteredStudents?.length === 0
                  ? "No students"
                  : "Select a student"}
              </option>
              {filteredStudents?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}
                </option>
              ))}
            </select>
            {!selectedSchoolId && <p className="caption mt-1">Select a school first</p>}
          </div>
        </div>

        {/* Row 2: Project, Lesson, Slide */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Project Dropdown */}
          <div>
            <label className="block label mb-2">Project</label>
            <select
              value={selectedProjectId}
              onChange={(e) => {
                setSelectedProjectId(e.target.value);
                setSelectedLessonId(""); // Reset lesson when project changes
                setSelectedSlideId(""); // Reset slide when project changes
              }}
              className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select a project</option>
              {filteredProjects?.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Lesson Dropdown */}
          <div>
            <label className="block label mb-2">Lesson</label>
            <select
              value={selectedLessonId}
              onChange={(e) => {
                setSelectedLessonId(e.target.value);
                setSelectedSlideId(""); // Reset slide when lesson changes
              }}
              disabled={!selectedProjectId}
              className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-900 disabled:cursor-not-allowed"
            >
              <option value="">Select a lesson</option>
              {filteredLessons?.map((lesson) => (
                <option key={lesson.id} value={lesson.id}>
                  {lesson.name}
                </option>
              ))}
            </select>
            {!selectedProjectId && (
              <p className="text-xs text-gray-500 mt-1">Select a project first</p>
            )}
          </div>

          {/* Slide Dropdown */}
          <div>
            <label className="block label mb-2">Slide</label>
            <select
              value={selectedSlideId}
              onChange={(e) => setSelectedSlideId(e.target.value)}
              disabled={!selectedLessonId}
              className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-900 disabled:cursor-not-allowed"
            >
              <option value="">Select a slide</option>
              {slidesData?.slidesByLessonId?.items?.map((slide) => (
                <option key={slide.id} value={slide.id}>
                  {slide.name}
                </option>
              ))}
            </select>
            {!selectedLessonId && (
              <p className="text-xs text-gray-500 mt-1">Select a lesson first</p>
            )}
          </div>
        </div>

        {/* Row 3: Other Users */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Other Users Dropdown */}
          <div>
            <label className="block label mb-2">Other Users</label>
            <select
              value={selectedOtherUserId}
              onChange={(e) => setSelectedOtherUserId(e.target.value)}
              className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select other user</option>
              {filteredOtherUsers?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Sub-Tab Navigation */}
      <ContentSubNav activeTab={activeSubTab} onTabChange={setActiveSubTab} />

      {/* Tab Content */}
      {activeSubTab === "overview" && <OverviewTab filters={filterState} />}
      {activeSubTab === "syllables" && <SyllablesTab filters={filterState} />}
      {activeSubTab === "words" && <WordsTab filters={filterState} />}
      {activeSubTab === "lessons" && <LessonsTab filters={filterState} />}
      {activeSubTab === "cards" && <CardsTab filters={filterState} />}
      {activeSubTab === "patterns" && <PatternsTab filters={filterState} />}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Retention Rate Chart */}
        {selectedStudentId && (
          <div className="panel p-6">
            <h2 className="h5 mb-4">Retention Rate Over Time</h2>
            <RetentionRateChart
              studentId={selectedStudentId}
              {...(selectedLessonId && { lessonId: selectedLessonId })}
            />
          </div>
        )}

        {/* Difficulty Distribution Chart - Only show when slide is selected */}
        {selectedSlideId && (
          <>
            <div className="panel p-6">
              <h2 className="h5 mb-4">Card Difficulty</h2>
              <DifficultyDistributionChart slideId={selectedSlideId} />
            </div>

            {/* Difficulty Explanation */}
            <div className="panel p-8">
              <h2 className="h5 mb-6">About Difficulty Rating</h2>
              <div className="caption leading-relaxed">
                <p className="mb-3">
                  The difficulty rating is calculated by the FSRS algorithm based on how hard a card
                  is for a student to remember:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-3">
                  <li>
                    <strong>0-3 (Easy):</strong> Student consistently remembers this card well
                  </li>
                  <li>
                    <strong>4-6 (Medium):</strong> Takes moderate effort to recall
                  </li>
                  <li>
                    <strong>7-10 (Hard):</strong> Student frequently forgets or struggles with this
                    card
                  </li>
                </ul>
                <p className="mb-2">
                  The algorithm automatically adjusts this rating based on the student's review
                  performance:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Repeatedly getting it wrong → difficulty increases</li>
                  <li>Consistently getting it right → difficulty decreases</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {/* Card State Breakdown Chart - Only show when lesson selected but NO slide */}
        {selectedLessonId && !selectedSlideId && (
          <div className="panel p-6">
            <h2 className="h5 mb-4">Card State Breakdown (All cards in lesson)</h2>
            <CardStateBreakdownChart
              lessonId={selectedLessonId}
              {...(selectedStudentId && { studentId: selectedStudentId })}
            />
          </div>
        )}
      </div>

      {/* Syllable Analytics - Show when slide is selected */}
      {selectedSlideId && (
        <div className="panel p-6">
          <h2 className="h5 mb-6">Syllable Analysis</h2>
          <SyllableAnalytics
            slideId={selectedSlideId}
            {...(selectedStudentId && { studentId: selectedStudentId })}
          />
        </div>
      )}

      {/* Review Compliance Table */}
      {selectedSchoolId && (
        <div className="panel p-6">
          <h2 className="text-lg font-medium text-secondary-200 mb-4">Review Compliance</h2>
          <ReviewComplianceTable schoolId={selectedSchoolId} />
        </div>
      )}

      {/* Placeholder message when no filters selected */}
      {!selectedStudentId && !selectedLessonId && !selectedSlideId && !selectedSchoolId && (
        <div className="panel p-12 text-center">
          <p className="text-gray-600">Please select filters above to view analytics</p>
        </div>
      )}
    </div>
  );
}
